import { useToken } from "@gluestack-style/react";
import { Textbox } from "components/form";
import { Box, HStack, Icon, Input, InputField, InputIcon, InputSlot, Pressable, Text, Typography } from "components/ui";
import { router, usePathname } from "expo-router";
import { ChevronDown, SearchIcon } from "lucide-react-native";
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, NativeMethods, ScrollView, View } from "react-native";
import { useDropdownComponent } from "utils/hooks/useDropdownComponent";
import { useIsMobile } from "utils/hooks/useIsMobile";

export interface SelectOption {
	id: string | number;
	label: string;
	value: any;
	[key: string]: any;
}

interface ButtonSelectProps {
	options: SelectOption[];
	value?: SelectOption[];
	defaultValue?: SelectOption[];
	onChange?: (selected: SelectOption[]) => void;
	label?: string;
	isFluid?: boolean;
	isMulti?: boolean;
	placeholder?: string;
	error?: string;
	disabled?: boolean;
	trigger?: ReactNode;
	customTrigger?: (props: { selectedItems: SelectOption[]; open: () => void; disabled: boolean; ref: React.RefObject<View & NativeMethods> }) => ReactNode;
	searchPlaceholder?: string;
	renderOption?: (option: SelectOption) => ReactNode;
	renderSelected?: (option: SelectOption) => ReactNode;
	filterOption?: (option: SelectOption, searchTerm: string) => boolean;
	variant?: "single" | "multiple";
	withFilter?: boolean;
}

const ButtonOption = ({
	option,
	handleSelect,
	renderOption,
	isSelected,
	resolvedPrimaryLight,
}: {
	option: SelectOption;
	handleSelect: (option: SelectOption) => void;
	renderOption?: (option: SelectOption) => ReactNode;
	isSelected: boolean;
	resolvedPrimaryLight: string;
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const resolvedNeutral200 = useToken("colors", "neutral100");
	return (
		<Pressable
			key={option.id}
			onPress={() => handleSelect(option)}
			onHoverIn={() => setIsHovered(true)}
			onHoverOut={() => setIsHovered(false)}
			style={{
				padding: 10,
				borderBottomWidth: 1,
				borderBottomColor: "#e6e6e6",
				// @ts-ignore
				transition: "all 0.1s ease-in-out",
				backgroundColor: isSelected ? resolvedPrimaryLight : isHovered ? resolvedNeutral200 : "transparent",
			}}
			accessibilityLabel={`Select ${option.label}`}
		>
			{renderOption ? renderOption(option) : <Text color={isSelected ? "white" : "black"}>{option.label}</Text>}
		</Pressable>
	);
};

export const ButtonSelect: FC<ButtonSelectProps> = ({
	options = [],
	value = [],
	defaultValue = [],
	onChange,
	placeholder = "Select...",
	label,
	isFluid = true,
	isMulti = false,
	error,
	disabled = false,
	trigger,
	customTrigger,
	searchPlaceholder = "Search...",
	renderOption,
	renderSelected,
	filterOption,
	variant = "single",
	withFilter = false,
}: ButtonSelectProps): JSX.Element => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const { setDropdownComponent } = useDropdownComponent();
	const [selectedItems, setSelectedItems] = useState<SelectOption[]>(value.length > 0 ? value : defaultValue);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownButtonRef = useRef<View & NativeMethods>(null);
	const dropdownRef = useRef<ScrollView>(null);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [buttonWidth, setButtonWidth] = useState(0);
	const isMobile = useIsMobile();
	const pathname = usePathname();
	const [originalPathname, setOriginalPathname] = useState<string>("");

	useEffect(() => {
		if (value) {
			setSelectedItems(value);
		}
	}, [value]);

	const filteredOptions = useMemo(() => {
		if (!options) return [];
		return options.filter((option) => {
			const matchesSearch = filterOption ? filterOption(option, searchTerm) : option.label.toLowerCase().includes(searchTerm.toLowerCase());
			return matchesSearch;
		});
	}, [options, searchTerm, filterOption]);

	const open = useCallback(() => {
		if (disabled) return;

		if (isMobile) {
			setIsOpen(true);
			setOriginalPathname(pathname);
			router.push({ pathname: "/dropdown" });
		} else {
			// @ts-ignore
			dropdownButtonRef.current?.measure((fx, fy, w, h, px, py) => {
				setPosition({ x: px, y: py + h });
				setButtonWidth(w);
				setIsOpen(true);
			});
		}
	}, [disabled, isMobile, pathname]);

	const close = useCallback(() => {
		setIsOpen(false);
		if (isMobile) {
			router.navigate({ pathname: originalPathname });
		}
	}, [isMobile, originalPathname]);

	const handleSelect = useCallback(
		(option: SelectOption) => {
			let newSelectedItems: SelectOption[];

			if (isMulti) {
				const isAlreadySelected = selectedItems.some((item) => item.id === option.id);
				if (isAlreadySelected) {
					newSelectedItems = selectedItems.filter((item) => item.id !== option.id);
				} else {
					newSelectedItems = [...selectedItems, option];
				}
			} else {
				newSelectedItems = [option];
			}

			setSelectedItems(newSelectedItems);

			if (onChange) {
				onChange(newSelectedItems);
			}

			if (!isMulti) {
				close();
			}
		},
		[isMulti, selectedItems, onChange, close],
	);

	const removeOption = useCallback(
		(optionToRemove: SelectOption) => {
			const newSelectedItems = selectedItems.filter((item) => item.id !== optionToRemove.id);
			setSelectedItems(newSelectedItems);
			if (onChange) {
				onChange(newSelectedItems);
			}
		},
		[selectedItems, onChange],
	);

	const clearSelection = useCallback(() => {
		setSelectedItems([]);
		if (onChange) {
			onChange([]);
		}
	}, [onChange]);

	const renderSelectedItems = useCallback(() => {
		if (variant === "single") {
			return selectedItems[0] ? renderSelected ? renderSelected(selectedItems[0]) : <Typography>{selectedItems[0].label}</Typography> : <Typography color="$neutral200">{placeholder}</Typography>;
		}

		return selectedItems.length > 0 ? (
			<HStack flexWrap="nowrap" gap={6}>
				{selectedItems.map((option) => (
					<HStack
						key={option.id}
						sx={{
							backgroundColor: "$neutral25",
							marginHorizontal: label ? 6 : 0,
							marginVertical: label ? 5 : 0,
						}}
					>
						{renderSelected ? renderSelected(option) : <Typography>{option.label}</Typography>}
						<Pressable
							sx={{
								borderColor: "$neutral25",
								marginRight: 4,
								justifyContent: "center",
								alignItems: "center",
								paddingVertical: 2,
								borderTopRightRadius: 4,
								borderBottomRightRadius: 4,
							}}
							onPress={() => removeOption(option)}
							accessibilityLabel={`Remove ${option.label}`}
						>
							<Icon as={ChevronDown} w={12} h={12} />
						</Pressable>
					</HStack>
				))}
			</HStack>
		) : (
			<Typography color="$neutral200">{placeholder}</Typography>
		);
	}, [selectedItems, label, removeOption, renderSelected, variant, placeholder]);

	const renderDropdownContent = useCallback(() => {
		if (filteredOptions.length === 0) {
			return (
				<Box padding={16} mt={16}>
					<Text>No options found.</Text>
				</Box>
			);
		}

		return filteredOptions.map((option) => {
			const resolvedPrimaryLight = useToken("colors", "primarylight");
			const isSelected = selectedItems.some((item) => item.id === option.id);
			return <ButtonOption key={option.id} option={option} handleSelect={handleSelect} renderOption={renderOption} isSelected={isSelected} resolvedPrimaryLight={resolvedPrimaryLight} />;
		});
	}, [filteredOptions, handleSelect, selectedItems, renderOption]);

	useEffect(() => {
		if (isMobile && isOpen) {
			setDropdownComponent(
				<ScrollView ref={dropdownRef} stickyHeaderIndices={[0]} style={{ flex: 1 }}>
					<Box
						backgroundColor="$primary"
						paddingHorizontal={16}
						paddingVertical={18}
						style={{
							position: "absolute",
							top: 0,
							zIndex: 1000,
						}}
					>
						<HStack space="xl" alignItems="center" mx="$0.5">
							<Pressable onPress={close} accessibilityLabel="Go back">
								<Icon color="$basicwhite" as={ChevronDown} m="$2" size="md" />
							</Pressable>
							{withFilter && (
								<Input
									flex={1}
									backgroundColor="$basicwhite"
									variant="outline"
									borderRadius={12}
									borderColor="$borderBlue"
									size="md"
									px="$0"
									py="$3"
									isDisabled={false}
									isInvalid={false}
									isReadOnly={false}
									style={{ zIndex: 100 }}
									isFocused={true}
									isHovered={true}
								>
									<InputSlot>
										<InputIcon as={SearchIcon} ml="$4" color="$basicblack" />
									</InputSlot>
									<InputField fontSize={"$h5desktop"} backgroundColor="white" placeholder={searchPlaceholder} value={searchTerm} onChangeText={setSearchTerm} color="$basicblack" placeholderTextColor={"#cccccc"} />
									{searchTerm.length > 0 && (
										<InputSlot>
											<Pressable onPress={() => setSearchTerm("")} accessibilityLabel="Clear search">
												<InputIcon as={ChevronDown} mr="$4" color="$basicblack" />
											</Pressable>
										</InputSlot>
									)}
								</Input>
							)}
						</HStack>
					</Box>
					<Box paddingBottom={100}>{renderDropdownContent()}</Box>
				</ScrollView>,
			);
		}
	}, [searchTerm, isMobile, isOpen, renderDropdownContent, withFilter, searchPlaceholder]);

	const defaultTrigger = (
		<Pressable
			ref={dropdownButtonRef}
			onPress={open}
			disabled={disabled}
			sx={{
				maxWidth: isMobile ? "auto" : 525,
				width: "$full",
				paddingHorizontal: 12,
				justifyContent: "center",
				paddingVertical: selectedItems.length > 0 ? 8 : 12,
				borderColor: error ? "$errordark" : "black",
				backgroundColor: "$basicwhite",
				borderWidth: 1,
				height: isMobile ? "auto" : "$full",
				borderRadius: 12,
				maxHeight: 50,
				position: "relative",
				opacity: disabled ? 0.6 : 1,
			}}
			accessibilityLabel={label ? `${label} dropdown` : "Select dropdown"}
			accessibilityHint={disabled ? "Dropdown is disabled" : "Double tap to open dropdown"}
		>
			{label && (
				<Typography
					sx={{
						top: isMobile ? -11 : -1,
						position: "absolute",
						zIndex: 1,
						lineHeight: 1.5,
						left: 11,
						backgroundColor: "white",
						paddingLeft: 6,
						fontSize: 13,
						fontWeight: 700,
						paddingRight: 6,
						color: error ? "$errordark" : undefined,
					}}
				>
					{label}:
				</Typography>
			)}
			{renderSelectedItems()}
			{error && <Typography sx={{ color: "$errordark", fontSize: 12, marginTop: 1 }}>{error}</Typography>}
		</Pressable>
	);

	const renderTrigger = () => {
		if (customTrigger) {
			return customTrigger({ selectedItems, open, disabled, ref: dropdownButtonRef });
		}
		if (trigger) {
			return trigger;
		}
		return defaultTrigger;
	};

	return (
		<>
			{renderTrigger()}
			{!isMobile && (
				<Modal onRequestClose={close} style={{ flex: 1, position: "relative" }} supportedOrientations={["portrait", "landscape"]} animationType="none" transparent={true} visible={isOpen}>
					<Pressable
						onPress={close}
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							bottom: 0,
							right: 0,
						}}
					/>
					<ScrollView
						ref={dropdownRef}
						stickyHeaderIndices={[0]}
						style={{
							position: "absolute",
							minWidth: 155,
							top: position.y + 5,
							left: position.x,
							width: buttonWidth,
							display: isOpen ? "flex" : "none",
							backgroundColor: "white",
							borderColor: "black",
							borderRadius: 12,
							zIndex: 9999,
							shadowColor: "#000",
							shadowOffset: {
								width: 0,
								height: 1,
							},
							shadowOpacity: 0.2,
							shadowRadius: 1.41,
							maxHeight: 300,
							elevation: 2,
						}}
					>
						{withFilter && (
							<Box
								style={{
									width: "100%",
									borderWidth: 0,
									backgroundColor: "white",
									position: "absolute",
									top: 0,
									zIndex: 1000,
								}}
							>
								<Textbox placeholder={searchPlaceholder} value={searchTerm} onChange={setSearchTerm} />
							</Box>
						)}
						{renderDropdownContent()}
					</ScrollView>
				</Modal>
			)}
		</>
	);
};
